import { IconButton, Popover, Typography, useTheme } from '@mui/material'
import {
    green,
    marketStatusBlue,
    marketStatusOpenBg,
    postMarketOpenBg,
    preMarketOpenBg,
    preMarketTextColor
} from '../../assets/colors'
import Morning from '../../assets/icons/Morning'
import MarketOpen from '../../assets/icons/MarketOpen'
import MarketClosed from '../../assets/icons/MarketClosed'
import PostClose from '../../assets/icons/PostClose'
import { ExchangeTradingSession, TradingSession } from '../../proto/generated/trading-calendar'
import MarketStatusBar from './Slider'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectPrimarySecurity } from '../../selectors/home'
import { NonInteractiveTooltip } from '../../components/custom'
import { selectExchangeTradingSessions, selectTradingCalendarDataByExchange } from '../../selectors/chart'
import { darkHoverBtnColor, lightHoverBtnColor } from '../../theme/theme'
import { constructCalendarDataLookup, getTradingSessionsForDate } from '../../util/chart'
import { toDayStart } from '../../util/dateTime'

const DAY_MS = 24 * 60 * 60 * 1000

// Calendar template sessions store times as seconds-from-midnight; anchor them to a concrete day (epoch ms).
const anchorTemplateToDay = (dayMs: number, template: TradingSession): TradingSession => ({
    ...template,
    preOpenStartTime: dayMs + (template.preOpenStartTime || 0) * 1000,
    preOpenEndTime: dayMs + (template.preOpenEndTime || 0) * 1000,
    startTime: dayMs + (template.startTime || 0) * 1000,
    endTime: dayMs + (template.endTime || 0) * 1000,
    postCloseStartTime: dayMs + (template.postCloseStartTime || 0) * 1000,
    postCloseEndTime: dayMs + (template.postCloseEndTime || 0) * 1000
})

const MarketStatus = () => {
    const theme = useTheme()
    const closedBg = theme.palette.mode === 'dark' ? darkHoverBtnColor : lightHoverBtnColor
    const { expressionMode, primary, underlying, expressionSecurities } = useSelector(selectPrimarySecurity)
    const mainSecurity = expressionMode ? expressionSecurities[0] : (underlying || primary)
    const exchangeTradingSessions = useSelector(selectExchangeTradingSessions)
    const tradingCalendarData = useSelector(selectTradingCalendarDataByExchange(mainSecurity?.exchange))
    const calendarData = useMemo(() => constructCalendarDataLookup(tradingCalendarData), [tradingCalendarData])
    const [marketOpen, setMarketOpen] = useState<boolean>()
    const [preMarketOpen, setPreMarketOpen] = useState<boolean>()
    const [postMarketOpen, setPostMarketOpen] = useState<boolean>()
    const [holiday, setHoliday] = useState<boolean>()
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
    const [message, setMessage] = useState<{ lead: string; highlight: string; trail?: string }>()
    const [displaySession, setDisplaySession] = useState<TradingSession>()
    const open = Boolean(anchorEl)
    const [updatedKey, setUpdatedKey] = useState<number>(0)

    const nextTradingDayStartMs = useCallback((nowMs: number) => {
        for (let d = 1; d <= 10; d++) {
            const dayMs = toDayStart(nowMs).getTime() + d * DAY_MS
            const sessions = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)
            if (sessions && sessions.length) return dayMs
        }
        return undefined
    }, [calendarData])

    // Epoch ms of the next pre-open: prefer the live exchange sessions, else derive from the trading calendar.
    const computeNextPreOpenMs = useCallback((nowMs: number, sessions: TradingSession[]): number | undefined => {
        const futurePreOpens = sessions.map((s) => s.preOpenStartTime).filter((t) => t > nowMs)
        if (futurePreOpens.length) return Math.min(...futurePreOpens)
        const dayMs = toDayStart(nowMs).getTime()
        const todayTemplate = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)?.[0]
        if (todayTemplate) {
            const pre = dayMs + ((todayTemplate.preOpenStartTime || todayTemplate.startTime) || 0) * 1000
            if (pre > nowMs) return pre
        }
        const nextDayMs = nextTradingDayStartMs(nowMs)
        if (nextDayMs) {
            const nextTemplate = getTradingSessionsForDate(Math.floor(nextDayMs / 1000), calendarData)?.[0]
            if (nextTemplate) return nextDayMs + ((nextTemplate.preOpenStartTime || nextTemplate.startTime) || 0) * 1000
        }
        return undefined
    }, [calendarData, nextTradingDayStartMs])

    // A session to draw the slider for when the live exchange data has none (e.g. weekends/holidays).
    const buildCalendarSession = useCallback((nowMs: number): TradingSession | undefined => {
        const dayMs = toDayStart(nowMs).getTime()
        const todayTemplate = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)?.[0]
        if (todayTemplate) return anchorTemplateToDay(dayMs, todayTemplate)
        // Non-trading day: collapse all windows to midnight so the bar renders as an empty (void) day.
        return {
            type: '',
            name: '',
            startTime: dayMs,
            endTime: dayMs,
            preOpenStartTime: dayMs,
            preOpenEndTime: dayMs,
            postCloseStartTime: dayMs,
            postCloseEndTime: dayMs
        }
    }, [calendarData])

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    useEffect(() => {
        if (!exchangeTradingSessions || !exchangeTradingSessions.length || !mainSecurity) return
        const exchangeTradingSession = exchangeTradingSessions.find((exchangeTradingSession: ExchangeTradingSession) => {
            return exchangeTradingSession.exchange === mainSecurity.exchange
        })
        const timeoutIds: NodeJS.Timeout[] = []
        const nowTime = Date.now()
        if (exchangeTradingSession) {
            for (let i = 0; i < exchangeTradingSession.tradingSessions.length; i++) {
                const tradingSession = exchangeTradingSession.tradingSessions[i]
                if(nowTime<tradingSession.preOpenStartTime) {
                    const diff = tradingSession.preOpenStartTime - nowTime
                    const id = setTimeout(() => {
                        setUpdatedKey(prevKey => prevKey + 1)
                    }, diff)
                    timeoutIds.push(id)
                }
                if(nowTime<tradingSession.preOpenEndTime) {
                    const diff = tradingSession.preOpenEndTime - nowTime
                    const id = setTimeout(() => {
                        setUpdatedKey(prevKey => prevKey + 1)
                    }, diff)
                    timeoutIds.push(id)
                }
                if(nowTime<tradingSession.startTime) {
                    const diff = tradingSession.startTime - nowTime
                    const id = setTimeout(() => {
                        setUpdatedKey(prevKey => prevKey + 1)
                    }, diff)
                    timeoutIds.push(id)
                }
                if(nowTime<tradingSession.endTime) {
                    const diff = tradingSession.endTime - nowTime
                    const id = setTimeout(() => {
                        setUpdatedKey(prevKey => prevKey + 1)
                    }, diff)
                    timeoutIds.push(id)
                }
            }
        }
        return () => {
            timeoutIds.forEach(id => clearTimeout(id))
        }
    }, [exchangeTradingSessions, mainSecurity])

    const formatDuration = (durationMs: number) => {
        const totalSeconds = Math.max(0, Math.floor(durationMs / 1000))
        const days = Math.floor(totalSeconds / 86400)
        const hours = Math.floor((totalSeconds % 86400) / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60
        const part = (value: number, unit: string) => `${value} ${unit}${value === 1 ? '' : 's'}`
        if (totalSeconds >= 86400) return `${part(days, 'day')} and ${part(hours, 'hour')}`
        if (totalSeconds >= 3600) return `${part(hours, 'hour')} and ${part(minutes, 'minute')}`
        if (totalSeconds >= 300) return part(minutes, 'minute')
        if (minutes === 0) return part(seconds, 'second')
        return `${part(minutes, 'minute')} and ${part(seconds, 'second')}`
    }

    useEffect(() => {
        const nowMs = Date.now()
        if (!mainSecurity) return
        const exchangeTradingSession = (exchangeTradingSessions || []).find((exchangeTradingSession: ExchangeTradingSession) => {
            return exchangeTradingSession.exchange === mainSecurity.exchange
        })
        const sessions = exchangeTradingSession?.tradingSessions || []
        let marketOpen = false
        let preMarketOpen = false
        let postMarketOpen = false
        let holiday = false
        let message: { lead: string; highlight: string; trail?: string } = { lead: '', highlight: '' }
        let activeSession: TradingSession | undefined

        for (const tradingSession of sessions) {
            if (nowMs >= tradingSession.preOpenStartTime && nowMs <= tradingSession.preOpenEndTime) {
                preMarketOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'The market is open for pre-market session.',
                    highlight: `Regular trading session will start in ${formatDuration(tradingSession.startTime - nowMs)}.`
                }
                break
            }
            if (nowMs >= tradingSession.startTime && nowMs <= tradingSession.endTime) {
                marketOpen = true
                activeSession = tradingSession
                const remainingMs = tradingSession.endTime - nowMs
                if (remainingMs < 30 * 60 * 1000) {
                    message = {
                        lead: 'Regular trading session ends in',
                        highlight: `${formatDuration(remainingMs)}.`,
                        trail: ' The post-close session will start after a 10-minute break.'
                    }
                } else {
                    message = {
                        lead: 'Regular trading session is active.',
                        highlight: `It will end in ${formatDuration(remainingMs)}.`
                    }
                }
                break
            }
            if (nowMs > tradingSession.endTime && nowMs < tradingSession.postCloseStartTime) {
                postMarketOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'Regular trading session has ended.',
                    highlight: `Post-close session begins in ${formatDuration(tradingSession.postCloseStartTime - nowMs)}.`
                }
                break
            }
            if (nowMs >= tradingSession.postCloseStartTime && nowMs <= tradingSession.postCloseEndTime) {
                postMarketOpen = true
                activeSession = tradingSession
                message = {
                    lead: 'Post-close session is active.',
                    highlight: `It will end in ${formatDuration(tradingSession.postCloseEndTime - nowMs)}.`
                }
                break
            }
        }

        if (!activeSession) {
            const nextPreOpenMs = computeNextPreOpenMs(nowMs, sessions)
            const openDuration = nextPreOpenMs ? formatDuration(nextPreOpenMs - nowMs) : ''
            // Trading holiday: a weekday with no scheduled sessions in the calendar (weekends are not "holidays").
            const dayMs = toDayStart(nowMs).getTime()
            const weekday = new Date(nowMs).getDay()
            const isWeekend = weekday === 0 || weekday === 6
            const todaySessions = getTradingSessionsForDate(Math.floor(dayMs / 1000), calendarData)
            holiday = !isWeekend && (!todaySessions || todaySessions.length === 0)
            message = holiday
                ? { lead: 'No trading sessions are scheduled today.', highlight: nextPreOpenMs ? `Market will open for pre-market trading in ${openDuration}.` : '' }
                : { lead: 'Market is closed.', highlight: nextPreOpenMs ? `It'll open for pre-market trading in ${openDuration}.` : '' }
        }

        const todayStr = new Date(nowMs).toDateString()
        const sameDaySession = sessions.find((s) => new Date(s.startTime).toDateString() === todayStr)
        const nextFutureSession = sessions
            .filter((s) => s.preOpenStartTime > nowMs)
            .sort((a, b) => a.preOpenStartTime - b.preOpenStartTime)[0]

        setMarketOpen(marketOpen)
        setPreMarketOpen(preMarketOpen)
        setPostMarketOpen(postMarketOpen)
        setHoliday(holiday)
        setMessage(message)
        setDisplaySession(activeSession || sameDaySession || nextFutureSession || sessions[0] || buildCalendarSession(nowMs))
    }, [exchangeTradingSessions, mainSecurity, updatedKey, computeNextPreOpenMs, buildCalendarSession, calendarData])

    useEffect(() => {
        if (!open) return
        setUpdatedKey((prev) => prev + 1)
        const id = setInterval(() => setUpdatedKey((prev) => prev + 1), 1000)
        return () => clearInterval(id)
    }, [open])

    const tooltipContent = useMemo(() => (
        <Typography
            variant="caption">{preMarketOpen ? 'Pre-market is open' : marketOpen ? 'Market Open' : postMarketOpen ? 'Post-close is open' : holiday ? 'Market Holiday' : 'Market Closed'}</Typography>
    ), [preMarketOpen, marketOpen, postMarketOpen, holiday])

    const tooltipChildren = useMemo(() => {
        return <IconButton
            style={{ padding: '0px' }}
            disableRipple={true}
            data-event={'Market Status Icon Clicked'}
            data-event-source={'stockbar'}
            onClick={handleClick}>
            <div
                style={{
                    display: 'flex',
                    borderRadius: '50%',
                    color: theme.palette.text.secondary,
                    backgroundColor: preMarketOpen ? preMarketOpenBg : marketOpen ? marketStatusOpenBg : postMarketOpen ? postMarketOpenBg : closedBg
                }}>
                {preMarketOpen ? <Morning /> : marketOpen ? <MarketOpen /> : postMarketOpen ? <PostClose /> : <MarketClosed />}
            </div>
        </IconButton>
    }, [handleClick, preMarketOpen, marketOpen, postMarketOpen, holiday, theme.palette.text.secondary, closedBg])

    const popover = useMemo(() => (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            style={{ marginLeft: '-16px', marginTop: '8px' }}
            slotProps={{
                paper: {
                    style: { borderRadius: '6px' }
                }
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
            }}
        >
            <div style={{ width: '340px', maxWidth: '340px' }}>
                <div style={{
                    display: 'flex',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    paddingLeft: '16px',
                    paddingRight: '28px'
                }}>
                    <div
                        style={{
                            display: 'flex',
                            alignSelf: 'flex-start',
                            padding: '0px',
                            borderRadius: '50%',
                            color: theme.palette.text.secondary,
                            backgroundColor: preMarketOpen ? preMarketOpenBg : marketOpen ? marketStatusOpenBg : postMarketOpen ? postMarketOpenBg : closedBg,
                            marginTop: '4px'
                        }}>
                        {preMarketOpen ? <Morning /> : marketOpen ? <MarketOpen /> : postMarketOpen ? <PostClose /> : <MarketClosed />}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '8px' }}>
                        <Typography variant="body1"
                                    style={{ fontWeight: 600 }}
                                    sx={{ color: preMarketOpen ? preMarketTextColor : marketOpen ? green : postMarketOpen ? marketStatusBlue : 'text.secondary' }}>{preMarketOpen ? 'Pre-market' : marketOpen ? 'Market open' : postMarketOpen ? 'Post-close' : holiday ? 'Market holiday' : 'Market closed'}</Typography>
                        <Typography variant="body2" sx={{ marginTop: '2px' }}>
                            {message?.lead}{message?.lead ? ' ' : ''}
                            <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{message?.highlight}</Typography>
                            {message?.trail}
                        </Typography>
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            marginTop: '16px'
                        }}>
                            {displaySession && <MarketStatusBar tradingSession={displaySession} />}
                        </div>
                    </div>
                </div>
            </div>
        </Popover>
    ), [open, anchorEl, handleClose, preMarketOpen, marketOpen, postMarketOpen, holiday, message, displaySession, theme.palette.text.secondary, closedBg])

    return (<>
        <NonInteractiveTooltip title={tooltipContent} arrow={true} placement="top">
            {tooltipChildren}
        </NonInteractiveTooltip>
        {popover}
    </>)
}
export default memo(MarketStatus)